import { Fight, FightEvent, FightEventFlag, FightSegment, SetupFightEvent } from 'web/data/models'
import { sortFightEvents } from 'web/utils/sort-utils'
import { assertDefined, assertInvariant, expectOneById, joinLines, lastItem, sortLinkedList, tryGetOneById, updateListItem } from 'web/utils/utils'
import { OutlineFactory } from './outline-factory'

type NewEvent = {
    name: string
    fightOffset: number
    segmentId: string
    userCreated: boolean
}
type NewSetupEvent = NewEvent & {
    setupId: string
}

type NewSegment = {
    parentSegmentId: string
    name?: string
}

export type TimeInterval = {
    minOffset: number
    maxOffset?: number
}

export type EditEvent = {
    eventId: string

    name?: string
    offset?: number
    comment?: string
    flags?: FightEventFlag[]
}

export type EditSegment = {
    segmentId: string

    name?: string
    offset?: number
    shiftSubsequentSegments?: boolean
    condition?: string
}

export class OutlineEngine {

    private fight: Fight
    private segments: FightSegment[]
    private events: FightEvent[]
    private setupFightEvents: SetupFightEvent[]

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Private Constructor
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    private constructor(parts: {
        fight: Fight
        segments: FightSegment[]
        events: FightEvent[]
        setupFightEvents: SetupFightEvent[]
    }) {
        this.fight = parts.fight
        this.segments = parts.segments
        this.events = parts.events
        this.setupFightEvents = parts.setupFightEvents
    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Static Public API
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    public static fromState(parts: {
        fight: Fight
        segments: FightSegment[]
        events: FightEvent[]
        setupFightEvents: SetupFightEvent[]
    }) {
        return new OutlineEngine(JSON.parse(JSON.stringify(parts)))
    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Public API
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    public finish(): {
        fight: Fight
        segments: FightSegment[]
        events: FightEvent[]
        setupFightEvents: SetupFightEvent[]
    } {

        this.pruneDeadSetupFightEvents()

        return {
            fight: this.fight,
            segments: sortLinkedList(this.segments),
            events: this.events.sort(sortFightEvents),
            setupFightEvents: this.setupFightEvents
        }
    }

    public createEvent(newEvent: NewEvent): OutlineEngine {
        const segment = this.getSegment(newEvent.segmentId)

        const event = OutlineFactory.createEvent({
            fightSegmentId: segment.id,
            fightOffset: newEvent.fightOffset,
            name: newEvent.name,
            userCreated: newEvent.userCreated,
            flags: []
        })

        this.events.push(event)
        return this
    }

    public createSetupEvent(newEvent: NewSetupEvent): OutlineEngine {
        const segment = this.getSegment(newEvent.segmentId)

        const event = OutlineFactory.createSetupEvent({
            fightSegmentId: segment.id,
            fightOffset: newEvent.fightOffset,
            name: newEvent.name,
            userCreated: newEvent.userCreated,
            setupId: newEvent.setupId,
            flags: []
        })

        this.events.push(event)
        return this
    }

    public createSegment({ name, parentSegmentId }: NewSegment): OutlineEngine {
        const parentSegment = this.getSegment(parentSegmentId)
        const parentEndOffset = this.getSegmentEndOffset(parentSegmentId)

        if (parentSegment.childId != null) {
            const childSegment = this.getSegment(parentSegment.childId)

            if (childSegment.offset === parentEndOffset + 1) {
                throw Error(joinLines([
                    `Tried to add a segment where a segment can't fit.`,
                    `${childSegment.name} is blocking it.`
                ]))
            }
        }

        const segment = OutlineFactory.createSegment({
            fightId: this.fight.id,
            name: name ?? `Segment ${this.segments.length + 1}`,
            userCreated: true,
            offset: parentEndOffset + 1
        })

        this.segments.push(segment)

        // Update parent with reference to child
        const previousChildId = parentSegment.childId
        parentSegment.childId = segment.id

        if (previousChildId != null) {
            segment.childId = previousChildId
        }

        return this
    }

    public editEvent({ eventId, ...props }: EditEvent): OutlineEngine {
        const event = this.getEvent(eventId)

        if (event.userCreated === false) {
            throw Error(`Cannot edit a system event`)
        }

        const updatedEvent = {
            ...event,
            ...props
        }

        this.events = updateListItem(this.events, updatedEvent)
        return this
    }

    public editSegment({ segmentId, ...props }: EditSegment): OutlineEngine {
        const segment = this.getSegment(segmentId)

        if (props.name != null) {
            segment.name = props.name
        }

        if (props.condition != null) {
            segment.condition = props.condition
        }

        if (props.offset != null) {
            const offsetDelta = props.offset - segment.offset

            if (props.shiftSubsequentSegments) {
                const descendantSegments = this.getSegmentDescendantsSorted(segment.id)

                for (const descendantSegment of descendantSegments) {
                    descendantSegment.offset += offsetDelta

                    this.getEventsForSegment(descendantSegment.id)
                        .forEach(event => event.offset += offsetDelta)

                    this.getFightSetupEventsForSegment(descendantSegment.id)
                        .forEach(event => event.offset += offsetDelta)
                }
            }

            segment.offset += offsetDelta
            this.getEventsForSegment(segment.id)
                .forEach(event => event.offset += offsetDelta)

            this.getFightSetupEventsForSegment(segment.id)
                .forEach(event => event.offset += offsetDelta)
        }

        return this
    }

    public removeSegment(segmentId: string): OutlineEngine {
        const segmentIndex = this.segments.findIndex(segment => segment.id === segmentId)
        const removedSegment = this.segments.splice(segmentIndex, 1)[0]

        assertInvariant(
            removedSegment != null,
            `Removed segment, but nothing was deleted, this should not happen.`,
            `SegmentId: ${segmentId}, Segments: [${this.segments}]`
        )

        assertInvariant(
            removedSegment.userCreated === true,
            `Cannot remove system-created segment: ${removedSegment.name}`
        )

        // Delete events
        this.events = this.events.filter(event => event.fightSegmentId !== segmentId)

        const maybeParent = this.segments.find(segment => segment.childId === segmentId)

        // If we're the last child, we're done here.
        if (removedSegment.childId == null) {

            // Just need to remind our parent we're gone
            if (maybeParent) {
                maybeParent.childId = undefined
            }

            return this
        }

        // If we did have children, we need to re-parent them
        const childSegment = this.getSegment(removedSegment.childId)
        assertDefined(maybeParent, `Segments with children must have parents`)

        // Re-parent the orphan
        maybeParent.childId = childSegment.id

        return this
    }

    public removeEvent(eventId: string): OutlineEngine {
        const eventIndex = this.events.findIndex(event => event.id === eventId)

        assertInvariant(
            this.events[eventIndex].userCreated === true,
            `Cannot remove system-created event: ${this.events[eventIndex].name}`
        )

        const removedEvent = this.events.splice(eventIndex, 1)[0]

        assertInvariant(
            removedEvent != null,
            `Removed event, but nothing was deleted, this should not happen.`,
            `EventId: ${eventId}, Events: [${this.events}]`
        )

        return this
    }

    public static utils = {

        getValidSegmentInterval(params: {
            segment: FightSegment
            allSegments: FightSegment[]
            allEvents: FightEvent[]
            includeMaxOffset?: boolean
        }): TimeInterval | undefined {

            const getEventsSorted = (segmentId: string) => {
                return allEvents
                    .filter(event => {
                        return event.fightSegmentId === segmentId
                    })
                    .sort(sortFightEvents)
            }

            const { segment, allSegments, allEvents } = params

            // System segment shall not be modified
            if (segment.userCreated === false) {
                return undefined
            }

            // Calculate when a segment can start the earliest and latest, based on
            // surrounding segments and their events.
            let validInterval: TimeInterval = { minOffset: 0 }

            // Get last event of previous segment, calculate it's offset + 1s, that's the min
            const parentSegment = allSegments.find(seg => seg.childId === segment.id)
            assertDefined(parentSegment)

            // Grab min-offset by checking parent
            const parentSegmentEvents = getEventsSorted(parentSegment.id)
            if (parentSegmentEvents.length >= 1) {
                const lastEvent = lastItem(parentSegmentEvents)
                validInterval.minOffset = lastEvent.offset
            } else {
                validInterval.minOffset = parentSegment.offset
            }

            // The max offset only exists if we want to move a segment independently of
            // its children.
            if (params.includeMaxOffset && segment.childId != null) {
                const ourEvents = getEventsSorted(segment.id)
                const childSegment = allSegments.find(seg => seg.id === segment.childId)
                assertDefined(childSegment)

                const childSegmentOffset = childSegment.offset
                let maxOffset = childSegmentOffset

                // If we have events, we need to account for their length, and adjust our
                // max offset so that our events can't overlap onto the next segment
                if (ourEvents.length >= 1) {
                    const ourLastEvent = lastItem(ourEvents)

                    // Take the distance between our last event and the next segment and
                    // add it to our current offset, that's our max
                    maxOffset = segment.offset + (childSegmentOffset - ourLastEvent.offset)
                }

                validInterval.maxOffset = maxOffset
            }

            return validInterval
        }

    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Private API
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    private getSegment(id: string): FightSegment {
        return expectOneById(this.segments, id)
    }

    private getEvent(id: string): FightEvent {
        return expectOneById(this.events, id)
    }

    private getEventsForSegment(segmentId: string): FightEvent[] {
        return this.events.filter(event => event.fightSegmentId === segmentId)
    }

    private getSegmentEndOffset(segmentId: string): number {
        const events = this.events.filter(event => event.fightSegmentId === segmentId)

        if (events.length <= 0) {
            return this.getSegment(segmentId).offset
        }

        return lastItem([...events].sort(sortFightEvents)).offset
    }

    private getSegmentDescendantsSorted(segmentId: string): FightSegment[] {
        const sortedSegments = sortLinkedList([...this.segments])
        const startIndex = sortedSegments.findIndex(segment => segment.id === segmentId)

        assertInvariant(
            startIndex >= 0,
            `Total list of segments did not contain the given segment: `,
            `${segmentId}, [${this.segments}]`
        )

        return sortedSegments.slice(startIndex + 1)
    }

    private getFightSetupEventsForSegment(segmentId: string): SetupFightEvent[] {
        return this.setupFightEvents.filter(ev => ev.fightSegmentId === segmentId)
    }

    /**
     * This removes any setup fight events that are dead for various
     * reasons. Currently this is ran on every `finish` call.
     *
     * This isn't the optimal way to do this. By running it on finish,
     * we permanently destroy events we could technically recover in a
     * subsequent operation. For example if we create a new segment,
     * and then delete that segment. Any events under that segment are
     * now gone, when in theory nothing should have been removed.
     *
     * I can't fix this at the moment because the reducer doesn't
     * currently have a "finish-editing" action. Each edit action is
     * assumed to be the final action. It has "end", but that's not a
     * safe action to hook onto as you can save the edited state way
     * before you call "end".
     *
     * To fix this, "end" should be deprecated and something akin to
     * "commit" should be introduced.
     */
    private pruneDeadSetupFightEvents() {

        // Delete any orphaned events
        this.setupFightEvents = this.setupFightEvents.filter(item => {
            if (tryGetOneById(this.segments, item.fightSegmentId) != null) {
                return true
            }

            console.debug(`deleting orphaned setup fight event ${item.name} ${item.id}`)
            return false
        })

        // Delete events that are outside their segment range
        this.setupFightEvents = this.setupFightEvents.filter(item => {
            const mySegment = this.getSegment(item.fightSegmentId)
            const segmentStart = mySegment.offset

            // No child means it's impossible for us to overlap
            // anything in the future
            if (mySegment.childId == null) {
                // Keep any items that are at or later than the
                // segment start.
                const keepItem = item.offset >= segmentStart

                if (keepItem === false) {
                    console.debug(`deleting setup fight event outside valid range ${item.name} ${item.id}`)
                }

                return keepItem
            }

            const segmentEnd = this.getSegment(mySegment.childId).offset

            // Keep any items that fall within two consecutive
            // segments.
            const keepItem = (item.offset >= segmentStart) && (item.offset <= segmentEnd)

            if (keepItem === false) {
                console.debug(`deleting setup fight event outside valid range name: ${item.name} setup: ${item.setupId}`)
            }

            return keepItem
        })

    }

}
